/**
 * Calculated-field formula helpers — client mirror of the backend
 * `form-engine/evaluate-formula.ts`. Keep the two in sync.
 *
 * A formula is an expression over:
 *   - field references:   {fieldId}
 *   - number literals:    42, 1.5
 *   - string literals:    "kg", " / "
 *   - arithmetic:         + − * / and parentheses
 *   - comparisons:        == != < <= > >=
 *   - logical:            AND OR  (case-insensitive; AND binds tighter than OR)
 *   - logical functions:  ANY(c1, c2, …) — true if any condition is true;
 *                         ALL(c1, c2, …) — true if every condition is true
 *   - conditional:        IF(condition, then_value, else_value)
 *   - aggregate functions: SUM(e1, e2, …) — sum of numeric args, blanks skipped;
 *                         MAX(e1, e2, …) — largest numeric arg, blanks skipped;
 *                         both are blank if every arg is blank; a non-numeric
 *                         text arg makes the whole result blank (like -, *, /)
 *   - math functions (case-insensitive):
 *       LOG10(x)          — base-10 logarithm. Blank when x ≤ 0 (log10 of a
 *                            non-positive number is undefined) or non-numeric.
 *       ABS(x)            — magnitude, blank when non-numeric.
 *       CEILING(x)        — smallest integer ≥ x.
 *       FLOOR(x)          — largest integer ≤ x.
 *       ROUND(x, digits)  — x rounded to `digits` decimals, half-away-from-zero
 *                            to match SQL Server (the legacy CGT database this
 *                            mirrors), so ROUND(2.345, 2) = 2.35 and
 *                            ROUND(-2.345, 2) = -2.35 — NOT JavaScript's
 *                            Math.round, which is half-up (-2.345 → -2.34).
 *                            `digits` must be a whole number from 0 to
 *                            MAX_ROUND_DIGITS (the engine's own output
 *                            precision); anything else is blank. This is
 *                            mid-expression rounding,
 *                            applied before any further arithmetic, and is
 *                            independent of the field's display Result format.
 *   - date/time (case-insensitive):
 *       TODAY()                     — today's calendar date, in the formula
 *                                      context's timezone (see FormulaContext)
 *       NOW()                       — the current date+time, same timezone
 *       DATEADD(date, amount, unit) — date/datetime plus `amount` `unit`s.
 *                                      amount may be negative. unit is one of
 *                                      "days" "hours" "minutes" "weeks"
 *                                      "months" "years" (quoted string).
 *                                      Adding hours/minutes always yields a
 *                                      date+time result; other units preserve
 *                                      the input's date-vs-datetime kind.
 *                                      Adding months/years clamps an
 *                                      overflowing day to the target month's
 *                                      last day (Jan 31 + 1 month → Feb 28/29),
 *                                      never rolling into the following month.
 *       DATEDIFF(start, end, unit)  — end − start in whole `unit`s (a plain
 *                                      number; negative when end < start).
 *                                      "days"/"weeks" compare calendar dates
 *                                      (time-of-day ignored on both sides —
 *                                      this is also what makes a date-only
 *                                      value behave as valid through the end
 *                                      of that calendar date in comparisons
 *                                      below). "months"/"years" count whole
 *                                      elapsed calendar units. "hours"/
 *                                      "minutes" use exact elapsed time.
 *
 * `+` is overloaded: when both operands are numeric it adds, otherwise it
 * concatenates. Adjacent operands without an explicit operator are joined
 * with an implicit `+`. `- * /` are numeric only — a date operand makes an
 * arithmetic (non-comparison) result blank; use DATEADD/DATEDIFF for date
 * math so the unit is always explicit.
 *
 * Date and Date & Time field values (and TODAY()/NOW()/DATEADD() results)
 * support == != < <= > >= against each other. When either side of a
 * comparison is a bare Date (no time component), BOTH sides are compared at
 * calendar-date granularity (time-of-day dropped) — a Date-only value is
 * therefore valid through the end of that calendar date, not just its exact
 * midnight instant.
 *
 * Truthiness: null and false are falsy; everything else is truthy — including
 * 0 and "" (CGT admins treat 0 as a valid result, not absence of value). A
 * comparison against null returns null, which coerces to false inside IF so
 * the else branch runs when either operand is missing.
 *
 * IF, AND, and OR are short-circuit: refs inside an un-evaluated branch do NOT
 * set the "missing → null" flag. Division by zero → null (unchanged).
 *
 * Parsing uses a hand-written recursive descent algorithm — no eval, no
 * new Function, no third-party expression library.
 *
 * All date arithmetic is naive calendar math anchored to UTC internally
 * (Date.UTC / getUTC*), never the evaluating machine's local timezone — the
 * browser and server must produce identical results regardless of host OS
 * timezone. The only place a real IANA timezone is consulted is TODAY()/NOW()
 * reading "the current instant" via FormulaContext.timeZone.
 *
 * IF(weight > 0, dose / weight, 0)        — basic conditional
 * IF(a > 0 AND b > 0, a / b, "N/A")      — logical combinator in condition
 * IF(a > 0, IF(b > 0, a / b, 0), 0)      — nested IF
 * DATEADD({draw_date}, 7, "days")        — a week after the draw date
 * DATEDIFF({start}, {end}, "days")       — whole days between two dates
 * ALL({days} >= 0, {days} <= 7, TODAY() <= {expiration})  — validity gate
 * LOG10({pre_tcells} / {post_tcells})    — T-cell log reduction (CD34 selection)
 * ABS(({a} - {b}) / (({a} + {b}) / 2) * 100)  — duplicate-count agreement, %
 * CEILING((({nc} * {vol}) / 714) / 70)   — aliquot count, always rounded up
 */

export type DisplayFormat = 'number' | 'decimal2' | 'decimal3' | 'date' | 'datetime'
export type FormulaValue = number | string | null

/** Evaluation-time inputs beyond the field-value map — currently just "what
 *  instant/timezone does TODAY()/NOW() resolve against". Optional; callers
 *  that never use TODAY()/NOW() can omit it entirely. */
export interface FormulaContext {
  /** Defaults to `new Date()`. Pass explicitly in tests for determinism. */
  now?: Date
  /** IANA zone name, e.g. "America/New_York". Defaults to 'UTC'. */
  timeZone?: string
}

/** Internal tagged date/datetime value — never part of the public
 *  `FormulaValue` surface. A DATEADD()/TODAY()/NOW() result, or a ref whose
 *  raw string matches the date/datetime storage format, is tagged so
 *  comparisons can use date semantics instead of numeric/string semantics.
 *  Unwrapped back to a plain ISO string before evaluateFormula returns. */
interface DateTag {
  readonly __date: 'date' | 'datetime'
  iso: string
}

function isDateTag(v: unknown): v is DateTag {
  return typeof v === 'object' && v !== null && '__date' in (v as Record<string, unknown>)
}

// Internal: booleans appear from comparison/logical nodes and are consumed
// by IF; DateTag appears from date refs/functions. Neither is ever returned
// from the public evaluateFormula API.
type Comparable = FormulaValue | DateTag
type InternalValue = boolean | Comparable

/** Operator symbols shown in the builder, mapped to their stored tokens. */
export const OPERATORS = [
  { symbol: '+', token: '+', label: 'Add / join' },
  { symbol: '−', token: '-', label: 'Subtract' },
  { symbol: '×', token: '*', label: 'Multiply' },
  { symbol: '÷', token: '/', label: 'Divide' },
  { symbol: '(', token: '(', label: 'Open bracket' },
  { symbol: ')', token: ')', label: 'Close bracket' },
] as const

/** Comparison operators available for use in IF conditions. */
export const COMPARISON_OPERATORS = [
  { symbol: '==', token: '==', label: 'Equal' },
  { symbol: '≠', token: '!=', label: 'Not equal' },
  { symbol: '<', token: '<', label: 'Less than' },
  { symbol: '≤', token: '<=', label: 'Less than or equal' },
  { symbol: '>', token: '>', label: 'Greater than' },
  { symbol: '≥', token: '>=', label: 'Greater than or equal' },
] as const

/** Units accepted by DATEADD/DATEDIFF's third argument (case-insensitive). */
export const DATE_UNITS = ['days', 'hours', 'minutes', 'weeks', 'months', 'years'] as const
export type DateUnit = (typeof DATE_UNITS)[number]

/** Single-argument math functions — `FN(x)`. ROUND is excluded because it
 *  takes a second (digits) argument; see MATH_FUNCTIONS for the authoring list. */
const MATH_FN_1 = ['LOG10', 'ABS', 'CEILING', 'FLOOR'] as const
type MathFn1 = (typeof MATH_FN_1)[number]

/** Every math function, in the order the builder offers them. */
export const MATH_FUNCTIONS = [...MATH_FN_1, 'ROUND'] as const
export type MathFunction = (typeof MATH_FUNCTIONS)[number]

/**
 * Largest `digits` ROUND accepts.
 *
 * evaluateFormula returns every number at 9-decimal precision (see the
 * `Math.round(result * 1e9) / 1e9` line at its output boundary). ROUND(x, 10)
 * would therefore compute a 10-decimal value and have that tenth decimal
 * rounded away on the way out — the author asked for a precision this engine
 * cannot return, and got a different number back with no indication why.
 * Reject it the same way a negative digit count is rejected. The builder
 * enforces the same ceiling so an unusable formula cannot be authored.
 */
export const MAX_ROUND_DIGITS = 9

// ─── Unicode display-operator normalization ──────────────────────────────────
// The builder's chip UI only ever emits canonical ASCII tokens, so the
// tokenizer below is intentionally ASCII-only. Excel import is the one
// authoring surface where a formula can arrive as free text instead — typed
// straight from what the UI *displays* (× ÷ − ≠ ≤ ≥). The old tokenizer
// silently dropped those symbols (no token = no error), which left adjacent
// operands with nothing between them; the parser's implicit-join rule (see
// parseAdd below) then joined them with a `+` — the reported bug ("× ÷" →
// "+ +"). normalizeFormulaOperatorSymbols must run before extractFormulaRefs/
// tokenize/parse on anything that didn't come through the builder's own UI.

/** Derived from OPERATORS/COMPARISON_OPERATORS above (skipping entries where
 *  symbol === token) so the display and storage vocabularies can never
 *  drift apart — this IS the "supported Unicode operators" list. */
const OPERATOR_SYMBOL_TO_TOKEN: ReadonlyMap<string, string> = new Map(
  [...OPERATORS, ...COMPARISON_OPERATORS]
    .filter(o => o.symbol !== o.token)
    .map(o => [o.symbol, o.token]),
)

/** Characters legal in a canonical (post-normalization) formula outside
 *  string literals: identifier/ref characters, the ASCII operator/punctuation
 *  set, decimal points, and whitespace. */
const CANONICAL_CHAR_RE = /[A-Za-z0-9_{}+\-*/(),<>=!.\s]/

/**
 * Replaces supported Unicode display operators (×, ÷, −, ≠, ≤, ≥) with their
 * canonical ASCII tokens, leaving `"..."` string-literal spans untouched (an
 * intentional Unicode character inside a quoted string is not an operator).
 * `{...}` reference spans pass through untouched too: a ref is a values-map
 * key, not operator text, and a table aggregate ref such as
 * `{tableId#col:colId:sum}` (see lib/tableForm.ts) carries `#` and `:`,
 * which are not canonical operator characters.
 * Any remaining character outside string literals that isn't part of the
 * canonical grammar is reported in `unsupported` rather than silently
 * dropped — callers must reject the formula when `unsupported` is non-empty,
 * never persist or evaluate it, since the tokenizer would otherwise
 * misinterpret the gap as an implicit `+` (the corruption this fixes).
 */
export function normalizeFormulaOperatorSymbols(formula: string): { formula: string; unsupported: string[] } {
  const pieces = formula.match(/"(?:[^"\\]|\\.)*"|\{[^}"]*\}|./gs) ?? []
  const unsupported = new Set<string>()
  let out = ''
  for (const piece of pieces) {
    if (piece[0] === '"' || (piece[0] === '{' && piece.length > 1)) {
      out += piece
      continue
    }
    const mapped = OPERATOR_SYMBOL_TO_TOKEN.get(piece)
    if (mapped) {
      out += mapped
      continue
    }
    out += piece
    if (!CANONICAL_CHAR_RE.test(piece)) unsupported.add(piece)
  }
  return { formula: out, unsupported: [...unsupported] }
}

// ─── Tokenizer ─────────────────────────────────────────────────────────────

// Order matters: word tokens (IF/AND/OR/date/math functions) and 2-char ops
// (==, !=, <=, >=) must appear before their substrings (<, >, =). Field refs
// and string literals come early so their inner characters aren't mis-split.
// LOG10 must precede the number alternative so its trailing "10" isn't split
// off as a numeric literal.
const TOKEN_RE =
  /\bTODAY\b|\bNOW\b|\bDATEADD\b|\bDATEDIFF\b|\bLOG10\b|\bABS\b|\bCEILING\b|\bFLOOR\b|\bROUND\b|\bIF\b|\bSUM\b|\bMAX\b|\bANY\b|\bALL\b|\bAND\b|\bOR\b|==|!=|<=|>=|[<>]|,|\{[^}]+\}|"(?:[^"\\]|\\.)*"|\d+(?:\.\d+)?|[+\-*/()]/gi

const REF_RE = /\{([^}]+)\}/g

export type TokenType = 'num' | 'str' | 'ref' | 'op' | 'lparen' | 'rparen' | 'func' | 'cmp' | 'logic' | 'comma'
export interface Token {
  type: TokenType
  value: string
}

/** Classify a single raw token string (as produced by `splitFormulaTokens`)
 *  into the same token-kind vocabulary the parser uses. Used by the chip
 *  builder to decide what inline editor a clicked token should get. */
export function classifyToken(raw: string): Token {
  return classify(raw)
}

function classify(raw: string): Token {
  const up = raw.toUpperCase()
  if (up === 'TODAY') return { type: 'func', value: 'TODAY' }
  if (up === 'NOW') return { type: 'func', value: 'NOW' }
  if (up === 'DATEADD') return { type: 'func', value: 'DATEADD' }
  if (up === 'DATEDIFF') return { type: 'func', value: 'DATEDIFF' }
  if (up === 'LOG10') return { type: 'func', value: 'LOG10' }
  if (up === 'ABS') return { type: 'func', value: 'ABS' }
  if (up === 'CEILING') return { type: 'func', value: 'CEILING' }
  if (up === 'FLOOR') return { type: 'func', value: 'FLOOR' }
  if (up === 'ROUND') return { type: 'func', value: 'ROUND' }
  if (up === 'IF') return { type: 'func', value: 'IF' }
  if (up === 'SUM') return { type: 'func', value: 'SUM' }
  if (up === 'MAX') return { type: 'func', value: 'MAX' }
  if (up === 'ANY') return { type: 'func', value: 'ANY' }
  if (up === 'ALL') return { type: 'func', value: 'ALL' }
  if (up === 'AND') return { type: 'logic', value: 'AND' }
  if (up === 'OR') return { type: 'logic', value: 'OR' }
  if (raw === ',') return { type: 'comma', value: ',' }
  if (raw === '==' || raw === '!=' || raw === '<' || raw === '<=' || raw === '>' || raw === '>=')
    return { type: 'cmp', value: raw }
  if (raw[0] === '{') return { type: 'ref', value: raw.slice(1, -1) }
  if (raw[0] === '"') return { type: 'str', value: unescapeString(raw) }
  if (raw === '(') return { type: 'lparen', value: raw }
  if (raw === ')') return { type: 'rparen', value: raw }
  if (raw === '+' || raw === '-' || raw === '*' || raw === '/')
    return { type: 'op', value: raw }
  return { type: 'num', value: raw }
}

/** Strip the surrounding quotes/escaping from a stored `"..."` string-literal
 *  token, for showing its raw text back in an edit field. */
export function unescapeString(raw: string): string {
  return raw.slice(1, -1).replace(/\\(["\\])/g, '$1')
}

/** Wrap a user-typed string as an escaped `"..."` literal token. */
export function toStringLiteral(text: string): string {
  return `"${text.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

/** Raw token strings, in order — used by the builder to display chips. */
export function splitFormulaTokens(formula: string): string[] {
  return formula.match(TOKEN_RE) ?? []
}

function tokenize(formula: string): Token[] {
  return splitFormulaTokens(formula).map(classify)
}

/** Field IDs referenced by a formula, first-seen order, de-duplicated. */
export function extractFormulaRefs(formula: string): string[] {
  const refs: string[] = []
  for (const m of formula.matchAll(REF_RE)) {
    if (!refs.includes(m[1])) refs.push(m[1])
  }
  return refs
}

// ─── AST ───────────────────────────────────────────────────────────────────

type Expr =
  | { k: 'num'; v: number }
  | { k: 'str'; v: string }
  | { k: 'ref'; id: string }
  | { k: 'neg'; operand: Expr }
  | { k: 'binop'; op: string; l: Expr; r: Expr }
  | { k: 'if'; cond: Expr; then: Expr; els: Expr }
  | { k: 'nary'; op: 'ANY' | 'ALL' | 'SUM' | 'MAX'; args: Expr[] }
  | { k: 'math1'; op: MathFn1; arg: Expr }
  | { k: 'round'; value: Expr; digits: Expr }
  | { k: 'today' }
  | { k: 'now' }
  | { k: 'dateadd'; date: Expr; amount: Expr; unit: Expr }
  | { k: 'datediff'; start: Expr; end: Expr; unit: Expr }

// ─── Parser ────────────────────────────────────────────────────────────────

/**
 * Recursive descent (Pratt-style precedence climbing) parser.
 * Grammar (lowest precedence first):
 *   expr      → or_expr
 *   or_expr   → and_expr ('OR' and_expr)*
 *   and_expr  → cmp_expr ('AND' cmp_expr)*
 *   cmp_expr  → add_expr (('==' | '!=' | '<' | '<=' | '>' | '>=') add_expr)?
 *   add_expr  → mul_expr (('+' | '-' | <implicit-join>) mul_expr)*
 *   mul_expr  → unary   (('*' | '/') unary)*
 *   unary     → '-' unary | '+' unary | primary
 *   primary   → IF '(' expr ',' expr ',' expr ')'
 *             | TODAY '(' ')' | NOW '(' ')'
 *             | DATEADD '(' expr ',' expr ',' expr ')'
 *             | DATEDIFF '(' expr ',' expr ',' expr ')'
 *             | (LOG10 | ABS | CEILING | FLOOR) '(' expr ')'
 *             | ROUND '(' expr ',' expr ')'
 *             | '(' expr ')'
 *             | ref | num | str
 */
function parse(tokens: Token[]): Expr | null {
  let pos = 0
  const peek = (): Token | undefined => tokens[pos]
  const consume = (): Token => tokens[pos++]

  function parseExpr(): Expr | null {
    return parseOr()
  }

  function parseOr(): Expr | null {
    let left = parseAnd()
    if (!left) return null
    while (peek()?.type === 'logic' && peek()!.value === 'OR') {
      consume()
      const right = parseAnd()
      if (!right) return null
      left = { k: 'binop', op: 'OR', l: left, r: right }
    }
    return left
  }

  function parseAnd(): Expr | null {
    let left = parseCmp()
    if (!left) return null
    while (peek()?.type === 'logic' && peek()!.value === 'AND') {
      consume()
      const right = parseCmp()
      if (!right) return null
      left = { k: 'binop', op: 'AND', l: left, r: right }
    }
    return left
  }

  function parseCmp(): Expr | null {
    const left = parseAdd()
    if (!left) return null
    if (peek()?.type === 'cmp') {
      const op = consume().value
      const right = parseAdd()
      if (!right) return null
      return { k: 'binop', op, l: left, r: right }
    }
    return left
  }

  function parseAdd(): Expr | null {
    let left = parseMul()
    if (!left) return null
    while (true) {
      const t = peek()
      if (t?.type === 'op' && (t.value === '+' || t.value === '-')) {
        const op = consume().value
        const right = parseMul()
        if (!right) return null
        left = { k: 'binop', op, l: left, r: right }
      } else if (t && isOperandStart(t)) {
        // implicit join: two adjacent operands concatenate via implicit '+'
        const right = parseMul()
        if (!right) return null
        left = { k: 'binop', op: '+', l: left, r: right }
      } else {
        break
      }
    }
    return left
  }

  function isOperandStart(t: Token): boolean {
    return (
      t.type === 'ref' ||
      t.type === 'num' ||
      t.type === 'str' ||
      t.type === 'lparen' ||
      t.type === 'func'
    )
  }

  function parseMul(): Expr | null {
    let left = parseUnary()
    if (!left) return null
    while (peek()?.type === 'op' && (peek()!.value === '*' || peek()!.value === '/')) {
      const op = consume().value
      const right = parseUnary()
      if (!right) return null
      left = { k: 'binop', op, l: left, r: right }
    }
    return left
  }

  function parseUnary(): Expr | null {
    const t = peek()
    if (t?.type === 'op' && t.value === '-') {
      consume()
      const operand = parseUnary()
      if (!operand) return null
      return { k: 'neg', operand }
    }
    if (t?.type === 'op' && t.value === '+') {
      consume() // unary plus is a no-op
      return parseUnary()
    }
    return parsePrimary()
  }

  function parsePrimary(): Expr | null {
    const t = peek()
    if (!t) return null

    if (t.type === 'func' && t.value === 'IF') {
      consume() // IF
      if (peek()?.type !== 'lparen') return null
      consume() // (
      const cond = parseExpr()
      if (!cond || peek()?.type !== 'comma') return null
      consume() // ,
      const then = parseExpr()
      if (!then || peek()?.type !== 'comma') return null
      consume() // ,
      const els = parseExpr()
      if (!els || peek()?.type !== 'rparen') return null
      consume() // )
      return { k: 'if', cond, then, els }
    }

    if (t.type === 'func' && (t.value === 'TODAY' || t.value === 'NOW')) {
      consume() // TODAY / NOW
      if (peek()?.type !== 'lparen') return null
      consume() // (
      if (peek()?.type !== 'rparen') return null
      consume() // )
      return t.value === 'TODAY' ? { k: 'today' } : { k: 'now' }
    }

    if (t.type === 'func' && t.value === 'DATEADD') {
      consume() // DATEADD
      if (peek()?.type !== 'lparen') return null
      consume() // (
      const date = parseExpr()
      if (!date || peek()?.type !== 'comma') return null
      consume() // ,
      const amount = parseExpr()
      if (!amount || peek()?.type !== 'comma') return null
      consume() // ,
      const unit = parseExpr()
      if (!unit || peek()?.type !== 'rparen') return null
      consume() // )
      return { k: 'dateadd', date, amount, unit }
    }

    if (t.type === 'func' && t.value === 'DATEDIFF') {
      consume() // DATEDIFF
      if (peek()?.type !== 'lparen') return null
      consume() // (
      const start = parseExpr()
      if (!start || peek()?.type !== 'comma') return null
      consume() // ,
      const end = parseExpr()
      if (!end || peek()?.type !== 'comma') return null
      consume() // ,
      const unit = parseExpr()
      if (!unit || peek()?.type !== 'rparen') return null
      consume() // )
      return { k: 'datediff', start, end, unit }
    }

    // LOG10(x) / ABS(x) / CEILING(x) / FLOOR(x) — fixed one-argument math
    // functions. Exactly one arg: a trailing comma is a malformed formula, not
    // an extra argument silently dropped.
    if (t.type === 'func' && MATH_FN_1.includes(t.value as MathFn1)) {
      consume() // LOG10 / ABS / CEILING / FLOOR
      if (peek()?.type !== 'lparen') return null
      consume() // (
      const arg = parseExpr()
      if (!arg || peek()?.type !== 'rparen') return null
      consume() // )
      return { k: 'math1', op: t.value as MathFn1, arg }
    }

    if (t.type === 'func' && t.value === 'ROUND') {
      consume() // ROUND
      if (peek()?.type !== 'lparen') return null
      consume() // (
      const value = parseExpr()
      if (!value || peek()?.type !== 'comma') return null
      consume() // ,
      const digits = parseExpr()
      if (!digits || peek()?.type !== 'rparen') return null
      consume() // )
      return { k: 'round', value, digits }
    }

    // ANY(c1, c2, …) / ALL(c1, c2, …) / SUM(e1, e2, …) / MAX(e1, e2, …) —
    // variadic functions, ≥1 arg. Logical (ANY/ALL) and numeric-aggregate
    // (SUM/MAX) share this parse path; they differ only in evaluation.
    if (
      t.type === 'func' &&
      (t.value === 'ANY' || t.value === 'ALL' || t.value === 'SUM' || t.value === 'MAX')
    ) {
      consume() // ANY / ALL / SUM / MAX
      if (peek()?.type !== 'lparen') return null
      consume() // (
      const args: Expr[] = []
      const first = parseExpr()
      if (!first) return null
      args.push(first)
      while (peek()?.type === 'comma') {
        consume() // ,
        const next = parseExpr()
        if (!next) return null
        args.push(next)
      }
      if (peek()?.type !== 'rparen') return null
      consume() // )
      return { k: 'nary', op: t.value as 'ANY' | 'ALL' | 'SUM' | 'MAX', args }
    }

    if (t.type === 'lparen') {
      consume()
      const inner = parseExpr()
      if (!inner || peek()?.type !== 'rparen') return null
      consume()
      return inner
    }

    if (t.type === 'ref') { consume(); return { k: 'ref', id: t.value } }
    if (t.type === 'num') { consume(); return { k: 'num', v: Number(t.value) } }
    if (t.type === 'str') { consume(); return { k: 'str', v: t.value } }

    return null
  }

  const result = parseExpr()
  // Fail on leftover tokens (malformed formula, e.g. a bare trailing operand)
  if (result !== null && pos !== tokens.length) return null
  return result
}

// ─── Date helpers ──────────────────────────────────────────────────────────
// All arithmetic below is naive calendar math anchored to UTC (Date.UTC /
// getUTC*) — the evaluating machine's local timezone must never leak in, so
// the browser and server always agree. The only real-timezone lookup is
// nowInZone(), which reads the current instant through an IANA zone to
// produce TODAY()/NOW()'s naive local date/time string.

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/

interface DateComponents {
  y: number
  mo: number // 1-based
  d: number
  h: number
  mi: number
}

function parseDateComponents(iso: string): DateComponents | null {
  const dt = DATE_TIME_RE.exec(iso)
  if (dt) return { y: +dt[1], mo: +dt[2], d: +dt[3], h: +dt[4], mi: +dt[5] }
  const d = DATE_ONLY_RE.exec(iso)
  if (d) return { y: +d[1], mo: +d[2], d: +d[3], h: 0, mi: 0 }
  return null
}

/** Tag a raw string as a DateTag if it matches the date/datetime storage
 *  format, else null. Pure format-sniffing (no field-type lookup) so this
 *  stays self-contained inside the formula engine. */
function tagFromString(raw: string): DateTag | null {
  if (DATE_TIME_RE.test(raw)) return { __date: 'datetime', iso: raw }
  if (DATE_ONLY_RE.test(raw)) return { __date: 'date', iso: raw }
  return null
}

function toDateTag(v: Comparable): DateTag | null {
  if (v === null) return null
  if (isDateTag(v)) return v
  if (typeof v === 'string') return tagFromString(v)
  return null
}

function pad(n: number, len = 2): string {
  return String(Math.trunc(n)).padStart(len, '0')
}

function componentsToTag(y: number, mo: number, d: number, h: number, mi: number, kind: 'date' | 'datetime'): DateTag {
  const datePart = `${pad(y, 4)}-${pad(mo)}-${pad(d)}`
  if (kind === 'date') return { __date: 'date', iso: datePart }
  return { __date: 'datetime', iso: `${datePart}T${pad(h)}:${pad(mi)}` }
}

function epochToTag(ms: number, kind: 'date' | 'datetime'): DateTag {
  const dt = new Date(ms)
  return componentsToTag(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate(), dt.getUTCHours(), dt.getUTCMinutes(), kind)
}

/** Full-precision epoch (time-of-day included; 0 for a date-only tag). */
function dateTagEpochMs(tag: DateTag): number {
  const c = parseDateComponents(tag.iso)
  if (!c) return NaN
  return Date.UTC(c.y, c.mo - 1, c.d, c.h, c.mi)
}

/** Calendar-date epoch — always strips time-of-day, even for a datetime tag.
 *  This is what implements "a Date-only value is valid through the end of
 *  that calendar date" when compared against a datetime like NOW(). */
function dateOnlyEpoch(tag: DateTag): number {
  const c = parseDateComponents(tag.iso)
  if (!c) return NaN
  return Date.UTC(c.y, c.mo - 1, c.d)
}

/** Last day-of-month for a 1-based month number. */
function daysInMonth(y: number, mo: number): number {
  return new Date(Date.UTC(y, mo, 0)).getUTCDate()
}

function addToDate(tag: DateTag, amount: number, unit: string): DateTag | null {
  const c = parseDateComponents(tag.iso)
  if (!c) return null
  switch (unit) {
    case 'minutes':
      return epochToTag(Date.UTC(c.y, c.mo - 1, c.d, c.h, c.mi) + amount * 60_000, 'datetime')
    case 'hours':
      return epochToTag(Date.UTC(c.y, c.mo - 1, c.d, c.h, c.mi) + amount * 3_600_000, 'datetime')
    case 'days':
      return epochToTag(Date.UTC(c.y, c.mo - 1, c.d, c.h, c.mi) + amount * 86_400_000, tag.__date)
    case 'weeks':
      return epochToTag(Date.UTC(c.y, c.mo - 1, c.d, c.h, c.mi) + amount * 7 * 86_400_000, tag.__date)
    case 'months': {
      const total = c.mo - 1 + amount
      const y2 = c.y + Math.floor(total / 12)
      const mo2 = (((total % 12) + 12) % 12) + 1
      const d2 = Math.min(c.d, daysInMonth(y2, mo2))
      return componentsToTag(y2, mo2, d2, c.h, c.mi, tag.__date)
    }
    case 'years': {
      const y2 = c.y + amount
      const d2 = Math.min(c.d, daysInMonth(y2, c.mo))
      return componentsToTag(y2, c.mo, d2, c.h, c.mi, tag.__date)
    }
    default:
      return null
  }
}

function diffDates(a: DateTag, b: DateTag, unit: string): number | null {
  const ca = parseDateComponents(a.iso)
  const cb = parseDateComponents(b.iso)
  if (!ca || !cb) return null
  switch (unit) {
    case 'minutes':
      return (Date.UTC(cb.y, cb.mo - 1, cb.d, cb.h, cb.mi) - Date.UTC(ca.y, ca.mo - 1, ca.d, ca.h, ca.mi)) / 60_000
    case 'hours':
      return (Date.UTC(cb.y, cb.mo - 1, cb.d, cb.h, cb.mi) - Date.UTC(ca.y, ca.mo - 1, ca.d, ca.h, ca.mi)) / 3_600_000
    case 'days':
      return (dateOnlyEpoch(b) - dateOnlyEpoch(a)) / 86_400_000
    case 'weeks':
      return (dateOnlyEpoch(b) - dateOnlyEpoch(a)) / 86_400_000 / 7
    case 'months': {
      let months = (cb.y - ca.y) * 12 + (cb.mo - ca.mo)
      if (cb.d < ca.d) months -= 1 // hasn't reached the day-of-month yet
      return months
    }
    case 'years': {
      let years = cb.y - ca.y
      if (cb.mo < ca.mo || (cb.mo === ca.mo && cb.d < ca.d)) years -= 1
      return years
    }
    default:
      return null
  }
}

/** "Now", read through an IANA zone, as both a date-only and a datetime tag. */
function nowInZone(ctx: FormulaContext | undefined): { date: DateTag; datetime: DateTag } {
  const now = ctx?.now ?? new Date()
  const timeZone = ctx?.timeZone ?? 'UTC'
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00'
  const y = Number(get('year'))
  const mo = Number(get('month'))
  const d = Number(get('day'))
  // Some engines render midnight as "24" with hour12:false.
  const h = Number(get('hour')) % 24
  const mi = Number(get('minute'))
  return {
    date: componentsToTag(y, mo, d, 0, 0, 'date'),
    datetime: componentsToTag(y, mo, d, h, mi, 'datetime'),
  }
}

// ─── Evaluation ────────────────────────────────────────────────────────────

/**
 * Truthiness rule for IF/AND/OR:
 *   falsy  → null, false
 *   truthy → everything else, including 0 and "" (they are valid CGT results)
 * A missing field ref evaluates to null and therefore coerces to false in a
 * condition, so the else branch runs when an operand is absent.
 */
function isTruthy(v: InternalValue): boolean {
  return v !== null && v !== false
}

function isNumericLike(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v)
  if (typeof v === 'string') {
    const s = v.trim()
    return s !== '' && Number.isFinite(Number(s))
  }
  return false
}

function toNum(v: Comparable): number | null {
  if (v === null) return null
  if (isDateTag(v)) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  return isNumericLike(v) ? Number(v) : null
}

function toStr(v: FormulaValue): string {
  return v === null ? '' : String(v)
}

// ─── Math helpers ──────────────────────────────────────────────────────────

/**
 * Multiply `n` by 10^exp using its decimal string form rather than floating
 * multiplication. `2.345 * 100` is 234.49999999999997 in binary floating point,
 * which would round DOWN — the exact case AC "ROUND(2.345, 2) = 2.35" tests.
 * Re-parsing "2.345e2" yields the nearest double to 234.5 instead.
 * An already-exponential string ("1e-7") has its exponent folded in, so the
 * result is never the invalid literal "1e-7e2".
 */
function shiftDecimalExponent(n: number, exp: number): number {
  if (!Number.isFinite(n)) return NaN
  const [mantissa, expPart] = String(n).split(/[eE]/)
  return Number(`${mantissa}e${(expPart ? Number(expPart) : 0) + exp}`)
}

/**
 * Round half AWAY FROM ZERO, matching SQL Server's ROUND() — the legacy CGT
 * database whose computed columns these formulas replace. JavaScript's
 * Math.round is half-UP, which differs on negatives (-2.5 → -2 rather than
 * -3), so it is only used here on a non-negative magnitude.
 */
function roundHalfAwayFromZero(x: number, digits: number): number {
  const shifted = shiftDecimalExponent(Math.abs(x), digits)
  if (!Number.isFinite(shifted)) return NaN
  const magnitude = shiftDecimalExponent(Math.round(shifted), -digits)
  return x < 0 ? -magnitude : magnitude
}

function applyMath1(op: MathFn1, x: number): number | null {
  switch (op) {
    // log10 of a non-positive number is undefined (and -Infinity at 0) — blank
    // rather than a value that would silently pass a release gate.
    case 'LOG10': return x > 0 ? Math.log10(x) : null
    case 'ABS': return Math.abs(x)
    case 'CEILING': return Math.ceil(x)
    case 'FLOOR': return Math.floor(x)
  }
}

function applyBinary(op: string, a: Comparable, b: Comparable): FormulaValue {
  if (a === null || b === null) return null
  // Dates don't support +/-/*// directly — DATEADD/DATEDIFF make the unit
  // explicit instead, per spec ("date subtraction should use DATEDIFF").
  if (isDateTag(a) || isDateTag(b)) return null
  if (op === '+') {
    if (typeof a === 'number' && typeof b === 'number') return a + b
    if (isNumericLike(a) && isNumericLike(b)) return Number(a) + Number(b)
    return toStr(a) + toStr(b)
  }
  const na = toNum(a)
  const nb = toNum(b)
  if (na === null || nb === null) return null
  switch (op) {
    case '-': return na - nb
    case '*': return na * nb
    case '/': return nb === 0 ? null : na / nb
    default:  return null
  }
}

/**
 * Comparison semantics:
 *   ==, != — case-sensitive equality on any type; null operand → null
 *   <, <=, >, >= — numeric or date/datetime only; null/incompatible → null
 * Date comparisons: if either side is a Date (no time), BOTH sides compare
 * at calendar-date granularity (time-of-day dropped on both) — see module
 * docblock for why.
 */
function applyComparison(op: string, a: Comparable, b: Comparable): boolean | null {
  if (a === null || b === null) return null

  if (isDateTag(a) || isDateTag(b)) {
    const ta = toDateTag(a)
    const tb = toDateTag(b)
    if (!ta || !tb) return null
    const dayGrain = ta.__date === 'date' || tb.__date === 'date'
    const ea = dayGrain ? dateOnlyEpoch(ta) : dateTagEpochMs(ta)
    const eb = dayGrain ? dateOnlyEpoch(tb) : dateTagEpochMs(tb)
    if (Number.isNaN(ea) || Number.isNaN(eb)) return null
    switch (op) {
      case '==': return ea === eb
      case '!=': return ea !== eb
      case '<':  return ea < eb
      case '<=': return ea <= eb
      case '>':  return ea > eb
      case '>=': return ea >= eb
      default:   return null
    }
  }

  if (op === '==') return a === b
  if (op === '!=') return a !== b
  // Numeric comparisons
  const na = toNum(a)
  const nb = toNum(b)
  if (na === null || nb === null) return null
  switch (op) {
    case '<':  return na < nb
    case '<=': return na <= nb
    case '>':  return na > nb
    case '>=': return na >= nb
    default:   return null
  }
}

function evalNode(expr: Expr, values: Record<string, unknown>, ctx: FormulaContext | undefined): InternalValue {
  switch (expr.k) {
    case 'num': return expr.v
    case 'str': return expr.v
    case 'ref': {
      const raw = values[expr.id]
      if (raw === undefined || raw === null || raw === '') return null
      if (typeof raw === 'string') {
        const tag = tagFromString(raw)
        if (tag) return tag
      }
      return isNumericLike(raw) ? Number(raw) : String(raw)
    }
    case 'today': return nowInZone(ctx).date
    case 'now': return nowInZone(ctx).datetime
    case 'dateadd': {
      const dateVal = evalNode(expr.date, values, ctx)
      const amountVal = evalNode(expr.amount, values, ctx)
      const unitVal = evalNode(expr.unit, values, ctx)
      const tag = toDateTag(dateVal as Comparable)
      const amount = toNum(amountVal as Comparable)
      const unit = typeof unitVal === 'string' ? unitVal.trim().toLowerCase() : null
      if (!tag || amount === null || !unit) return null
      return addToDate(tag, amount, unit)
    }
    case 'datediff': {
      const startVal = evalNode(expr.start, values, ctx)
      const endVal = evalNode(expr.end, values, ctx)
      const unitVal = evalNode(expr.unit, values, ctx)
      const start = toDateTag(startVal as Comparable)
      const end = toDateTag(endVal as Comparable)
      const unit = typeof unitVal === 'string' ? unitVal.trim().toLowerCase() : null
      if (!start || !end || !unit) return null
      return diffDates(start, end, unit)
    }
    // LOG10/ABS/CEILING/FLOOR: a blank, non-numeric or date argument blanks
    // the result (consistent with -, *, /), so a gate reading it fails closed.
    case 'math1': {
      const n = toNum(evalNode(expr.arg, values, ctx) as Comparable)
      return n === null ? null : applyMath1(expr.op, n)
    }
    // ROUND(x, digits): `digits` must be 0..MAX_ROUND_DIGITS. A negative digit
    // count is SQL Server's "round to tens/hundreds", which no CGT formula
    // uses; above MAX_ROUND_DIGITS the requested precision cannot survive this
    // engine's output boundary. Reject both rather than guess at the intent or
    // silently return a differently-rounded number.
    case 'round': {
      const x = toNum(evalNode(expr.value, values, ctx) as Comparable)
      const digits = toNum(evalNode(expr.digits, values, ctx) as Comparable)
      if (x === null || digits === null || digits < 0 || digits > MAX_ROUND_DIGITS) return null
      const result = roundHalfAwayFromZero(x, Math.trunc(digits))
      return Number.isFinite(result) ? result : null
    }
    case 'neg': {
      const v = evalNode(expr.operand, values, ctx)
      const n = toNum(v as Comparable)
      return n === null ? null : -n
    }
    // Short-circuit AND: if left is falsy, skip right (it may reference missing fields)
    case 'binop': {
      if (expr.op === 'AND') {
        const left = evalNode(expr.l, values, ctx)
        if (!isTruthy(left)) return false
        return isTruthy(evalNode(expr.r, values, ctx))
      }
      // Short-circuit OR: if left is truthy, skip right
      if (expr.op === 'OR') {
        const left = evalNode(expr.l, values, ctx)
        if (isTruthy(left)) return true
        return isTruthy(evalNode(expr.r, values, ctx))
      }
      const left = evalNode(expr.l, values, ctx) as Comparable
      const right = evalNode(expr.r, values, ctx) as Comparable
      if (['==', '!=', '<', '<=', '>', '>='].includes(expr.op)) {
        return applyComparison(expr.op, left, right)
      }
      return applyBinary(expr.op, left, right)
    }
    // Short-circuit IF: evaluate only the taken branch so missing refs in the
    // other branch do not nullify the result.
    case 'if': {
      const cond = evalNode(expr.cond, values, ctx)
      return isTruthy(cond) ? evalNode(expr.then, values, ctx) : evalNode(expr.els, values, ctx)
    }
    // ANY/ALL short-circuit left-to-right like chained OR/AND.
    case 'nary': {
      if (expr.op === 'ANY') {
        for (const a of expr.args) if (isTruthy(evalNode(a, values, ctx))) return true
        return false
      }
      if (expr.op === 'ALL') {
        for (const a of expr.args) if (!isTruthy(evalNode(a, values, ctx))) return false
        return true
      }
      // SUM/MAX: blank args are skipped; a non-numeric arg blanks the whole
      // result (consistent with -, *, /); blank only if every arg is blank.
      const nums: number[] = []
      for (const a of expr.args) {
        const v = evalNode(a, values, ctx) as Comparable
        if (v === null) continue
        const n = toNum(v)
        if (n === null) return null
        nums.push(n)
      }
      if (nums.length === 0) return null
      return expr.op === 'SUM' ? nums.reduce((s, n) => s + n, 0) : Math.max(...nums)
    }
  }
}

/**
 * Evaluate a formula against a value map. Returns `null` (render as "—") when
 * a referenced field in the evaluated path is missing/empty, an arithmetic
 * operand is non-numeric, division by zero occurs, or the expression is
 * malformed. Never throws.
 *
 * IF, AND, and OR short-circuit: refs inside an un-evaluated branch do NOT
 * cause a null result.
 *
 * A Date/DateTime result (from a ref, TODAY(), NOW(), or DATEADD()) comes
 * back as its plain ISO string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) — same shape
 * as a stored date field value.
 */
export function evaluateFormula(
  formula: string | undefined,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): FormulaValue {
  if (!formula || !formula.trim()) return null
  const ast = parse(tokenize(formula))
  if (!ast) return null
  const result = evalNode(ast, values, ctx)
  // Booleans from bare comparisons are not valid field display values
  if (typeof result === 'boolean') return null
  if (result === null) return null
  if (isDateTag(result)) return result.iso
  if (typeof result === 'number')
    return Number.isFinite(result) ? Math.round(result * 1e9) / 1e9 : null
  return result
}

/**
 * Evaluate a Boolean gate formula — used for field visibility/unlock,
 * signature completion, and whole-form completion gates. Fails CLOSED: a
 * blank/missing referenced field, a malformed formula, or any other null
 * result all mean the gate is locked (`false`), never left ambiguous. This
 * is deliberately stricter than `evaluateFormula`'s "stay blank" behavior —
 * a gate must resolve to a definite allow/deny.
 */
export function evaluateGateFormula(
  formula: string | undefined,
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): boolean {
  if (!formula || !formula.trim()) return false
  const ast = parse(tokenize(formula))
  if (!ast) return false
  return isTruthy(evalNode(ast, values, ctx))
}

// ─── Multi-field helpers ────────────────────────────────────────────────────
// Calculated fields are never written into submission.values — they're pure,
// read-only derivations recomputed on every render (see FormRenderer.tsx).
// That means a calculated field's raw `values` map normally does NOT contain
// other calculated fields' results, so a calculated field referencing
// ANOTHER calculated field (chained dependencies, e.g. "days between two
// calculated dates") would see a blank operand and render "—" — even though
// the backend resolves this correctly via its own mirror of these two
// functions (form-engine/evaluate-formula.ts) once a save round-trips.
// recomputeCalculated closes that gap client-side: call it once to build an
// augmented values map with every calculated field's current result filled
// in, then use THAT map for rendering, further calculated fields, and any
// formula gate (visibility/unlock/signature/completion) that might reference
// a calculated field.

// ─── Authoring-time analysis ────────────────────────────────────────────────
// Design-time only: these inspect a formula's SHAPE without evaluating it, so
// the Form Builder can tell an author whether the gate they just typed is
// sound. Nothing here runs at fill time — evaluateGateFormula stays the single
// run-time authority, and its fail-closed contract is unchanged.

/**
 * What a parsed formula yields:
 *   'boolean' — a comparison, AND/OR, or ANY()/ALL(): a definite true/false.
 *   'value'   — anything else (a bare ref, arithmetic, IF, a date function).
 *               Gates read these for truthiness: null/blank is false, any
 *               other value — including 0 — is true (see isTruthy).
 */
export type FormulaShape = 'boolean' | 'value'

export interface FormulaAnalysis {
  /** True when the formula parses into one complete expression. */
  parsed: boolean
  /** Result shape, when it parsed. */
  shape: FormulaShape | null
  /** Human-readable syntax problem, when it did not. */
  syntaxError: string | null
}

const BOOLEAN_BINOPS: ReadonlySet<string> = new Set([...COMPARISON_OPERATORS.map(o => o.token), 'AND', 'OR'])

function shapeOf(expr: Expr): FormulaShape {
  if (expr.k === 'binop') return BOOLEAN_BINOPS.has(expr.op) ? 'boolean' : 'value'
  if (expr.k === 'nary') return expr.op === 'ANY' || expr.op === 'ALL' ? 'boolean' : 'value'
  return 'value'
}

/**
 * Best-effort explanation of why `parse` rejected a formula. The parser itself
 * reports only null (39 distinct bail-outs, no position), so rather than thread
 * error state through it this re-scans the token stream for the handful of
 * mistakes authors actually make. Anything unrecognised falls back to a generic
 * message — never a wrong-but-specific one.
 */
function describeSyntaxError(tokens: Token[]): string {
  const openIndexes: number[] = []
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].type === 'lparen') openIndexes.push(i)
    else if (tokens[i].type === 'rparen') {
      if (openIndexes.length === 0) return 'Unexpected closing parenthesis.'
      openIndexes.pop()
    }
  }
  if (openIndexes.length > 0) {
    const prev = tokens[openIndexes[openIndexes.length - 1] - 1]
    return prev?.type === 'func'
      ? `Unbalanced parenthesis after ${prev.value}(.`
      : 'Unbalanced parenthesis.'
  }

  const last = tokens[tokens.length - 1]
  if (last?.type === 'comma') return 'Formula ends after a comma.'
  if (last?.type === 'op' || last?.type === 'cmp') return `Formula ends after "${last.value}".`
  if (last?.type === 'logic') return `Formula ends after ${last.value}.`

  return "This formula can't be read. Check its operators and parentheses."
}

/**
 * Parse a formula and report whether it is well-formed and what shape it
 * returns. Operator symbols are normalised first, so a stored `×`/`÷` spelling
 * is analysed as the `*`/`/` the tokenizer would otherwise drop.
 * A blank formula is `{ parsed: false, syntaxError: null }` — absent, not wrong.
 */
export function analyzeFormula(formula: string | undefined): FormulaAnalysis {
  if (!formula || !formula.trim()) return { parsed: false, shape: null, syntaxError: null }
  const { formula: canonical, unsupported } = normalizeFormulaOperatorSymbols(formula)
  // Must precede parsing, not merely explain a parse failure: TOKEN_RE matches
  // known tokens and skips everything else, so a stray character is DROPPED and
  // the gap is read as an implicit join — `{a} ~ 1` would parse happily as
  // `{a} 1`. normalizeFormulaOperatorSymbols' contract is that callers reject
  // on `unsupported` rather than evaluate the corrupted reading.
  if (unsupported.length > 0)
    return {
      parsed: false,
      shape: null,
      syntaxError: `Unsupported character: ${unsupported.map(c => `"${c}"`).join(', ')}.`,
    }
  const tokens = tokenize(canonical)
  const ast = parse(tokens)
  if (!ast) return { parsed: false, shape: null, syntaxError: describeSyntaxError(tokens) }
  return { parsed: true, shape: shapeOf(ast), syntaxError: null }
}

/** Minimal field shape these helpers need — allFields(definition) already fits. */
export interface CalcField {
  id: string
  label: string
  fieldType: string
  formula?: string
}

/**
 * Topologically order the calculated fields so a field is always evaluated
 * after every calculated field it depends on. Non-calculated fields are ignored.
 *
 * @throws Error when a dependency cycle is detected (A → B → A).
 */
export function topoSortCalculated(fields: CalcField[]): CalcField[] {
  const calcById = new Map<string, CalcField>()
  for (const f of fields) {
    if (f.fieldType === 'calculated') calcById.set(f.id, f)
  }

  const ordered: CalcField[] = []
  const state = new Map<string, 'visiting' | 'done'>()

  const visit = (field: CalcField, trail: string[]) => {
    const status = state.get(field.id)
    if (status === 'done') return
    if (status === 'visiting') {
      const cycle = [...trail.slice(trail.indexOf(field.id)), field.id]
      throw new Error(`Circular formula dependency: ${cycle.join(' → ')}`)
    }

    state.set(field.id, 'visiting')
    for (const refId of extractFormulaRefs(field.formula ?? '')) {
      const dep = calcById.get(refId)
      if (dep) visit(dep, [...trail, field.id])
    }
    state.set(field.id, 'done')
    ordered.push(field)
  }

  for (const field of calcById.values()) visit(field, [])
  return ordered
}

/**
 * Recompute every calculated field in dependency order, returning an
 * augmented *copy* of `values` with each calculated field's current result
 * filled in. A field whose formula throws (cycle) is treated as if it had no
 * calculated fields at all — callers should not rely on recompute for
 * cycle *rejection*, only for cycle *tolerance*; save-time validation (both
 * repos) is what actually rejects a cyclic formula.
 */
export function recomputeCalculated(
  fields: CalcField[],
  values: Record<string, unknown>,
  ctx?: FormulaContext,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...values }
  let ordered: CalcField[]
  try {
    ordered = topoSortCalculated(fields)
  } catch {
    return next
  }
  for (const field of ordered) {
    next[field.id] = evaluateFormula(field.formula, next, ctx)
  }
  return next
}

/** Format a computed value for display. `null` → em dash; strings pass through. */
export function formatCalculated(
  value: FormulaValue,
  displayFormat?: DisplayFormat,
): string {
  if (value === null) return '—'
  if (typeof value === 'string') {
    if (displayFormat === 'date' || displayFormat === 'datetime') {
      const tag = tagFromString(value)
      if (tag) return formatDateForDisplay(tag)
    }
    return value
  }
  switch (displayFormat) {
    case 'number':   return String(Math.round(value))
    case 'decimal2': return value.toFixed(2)
    case 'decimal3': return value.toFixed(3)
    default:         return String(Math.round(value * 1000) / 1000)
  }
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Aug 15, 2026" / "Aug 15, 2026 2:30 PM" — display-only, from the tagged
 *  UTC-anchored components (never a real-timezone conversion). */
function formatDateForDisplay(tag: DateTag): string {
  const c = parseDateComponents(tag.iso)
  if (!c) return tag.iso
  const datePart = `${MONTH_ABBR[c.mo - 1]} ${c.d}, ${c.y}`
  if (tag.__date === 'date') return datePart
  const h12 = c.h % 12 === 0 ? 12 : c.h % 12
  const ampm = c.h < 12 ? 'AM' : 'PM'
  return `${datePart} ${h12}:${pad(c.mi)} ${ampm}`
}

// ─── Human-readable rendering ──────────────────────────────────────────────

const HUMAN_OP: Record<string, string> = {
  '+': '+', '-': '−', '*': '×', '/': '÷',
  '==': '=', '!=': '≠', '<': '<', '<=': '≤', '>': '>', '>=': '≥',
  'AND': 'and', 'OR': 'or',
}

function exprToHuman(expr: Expr, labelById: Record<string, string>): string {
  switch (expr.k) {
    case 'num':   return String(expr.v)
    case 'str':   return `"${expr.v}"`
    case 'ref':   return labelById[expr.id] ?? '⚠ deleted field'
    case 'neg':   return `−${exprToHuman(expr.operand, labelById)}`
    case 'today': return 'today'
    case 'now':   return 'now'
    case 'dateadd': {
      const date = exprToHuman(expr.date, labelById)
      const amount = exprToHuman(expr.amount, labelById)
      const unit = exprToHuman(expr.unit, labelById)
      return `${date} + ${amount} ${unit}`
    }
    case 'datediff': {
      const start = exprToHuman(expr.start, labelById)
      const end = exprToHuman(expr.end, labelById)
      const unit = exprToHuman(expr.unit, labelById)
      return `${unit} between ${start} and ${end}`
    }
    case 'if': {
      const cond = exprToHuman(expr.cond, labelById)
      const then = exprToHuman(expr.then, labelById)
      const els  = exprToHuman(expr.els, labelById)
      return `if ${cond} then ${then} else ${els}`
    }
    case 'math1': {
      const MATH1_HUMAN: Record<MathFn1, string> = {
        LOG10: 'log₁₀ of',
        ABS: 'absolute value of',
        CEILING: 'round up',
        FLOOR: 'round down',
      }
      return `${MATH1_HUMAN[expr.op]} (${exprToHuman(expr.arg, labelById)})`
    }
    case 'round': {
      const value = exprToHuman(expr.value, labelById)
      const digits = exprToHuman(expr.digits, labelById)
      return `round (${value}) to ${digits} decimals`
    }
    case 'nary': {
      const args = expr.args.map(a => exprToHuman(a, labelById)).join(', ')
      const NARY_HUMAN: Record<typeof expr.op, string> = {
        ANY: 'any of',
        ALL: 'all of',
        SUM: 'sum of',
        MAX: 'max of',
      }
      return `${NARY_HUMAN[expr.op]} (${args})`
    }
    case 'binop': {
      const l  = exprToHuman(expr.l, labelById)
      const r  = exprToHuman(expr.r, labelById)
      const op = HUMAN_OP[expr.op] ?? expr.op
      return `${l} ${op} ${r}`
    }
  }
}

/**
 * Render a formula as a human-readable summary using field labels, e.g.
 * "CD34 Count ÷ Patient Weight" or "if Weight > 0 then Dose ÷ Weight else 0".
 * Unknown IDs (deleted fields) render as a warning marker.
 * When the formula doesn't parse (e.g. mid-edit), falls back to token-level
 * label substitution so raw field IDs never surface in the UI.
 */
export function formulaToHuman(
  formula: string,
  labelById: Record<string, string>,
): string {
  if (!formula.trim()) return ''
  const ast = parse(tokenize(formula))
  if (!ast) {
    return splitFormulaTokens(formula)
      .map(tok => {
        const m = tok.match(/^\{(.+)\}$/)
        if (m) return labelById[m[1]] ?? '⚠ deleted field'
        return HUMAN_OP[tok] ?? tok
      })
      .join(' ')
  }
  return exprToHuman(ast, labelById)
}
